import EditCommand from "@app/util/undo/EditCommand";
import EditorState from "@app/util/undo/EditorState";
import { addInMap, getInitialState, getTimestamp } from "@app/util/undo/utils";

export const edit = <T>(
  state: EditorState<T>,
  command: EditCommand<T>,
  batchThresholdMs?: number
): EditorState<T> => ({
  ...state,
  appliedCommands: addInMap(state.appliedCommands, getTimestamp(batchThresholdMs), command),
  data: command.apply(state.data),
  hasChanges: true,
  revertedChanges: new Map(), // adding any new command erases redo array
});

export const undo = <T>(state: EditorState<T>): EditorState<T> => {
  if (!state.appliedCommands.size) {
    return state;
  }

  const lastTimestamp = Math.max(...state.appliedCommands.keys());
  const lastCommands = state.appliedCommands.get(lastTimestamp)!;
  const appliedCommands = new Map(state.appliedCommands);
  appliedCommands.delete(lastTimestamp);

  return {
    ...state,
    appliedCommands,
    // revert from right to left
    data: lastCommands.reduceRight((paths, command) => command.revert(paths), state.data),
    hasChanges: appliedCommands.size > 0,
    revertedChanges: addInMap(state.revertedChanges, lastTimestamp, ...lastCommands),
  };
};

export const redo = <T>(state: EditorState<T>): EditorState<T> => {
  if (!state.revertedChanges.size) {
    return state;
  }

  const lastTimestamp = Math.min(...state.revertedChanges.keys());
  const lastCommands = state.revertedChanges.get(lastTimestamp)!;
  const revertedChanges = new Map(state.revertedChanges);
  revertedChanges.delete(lastTimestamp);

  return {
    ...state,
    appliedCommands: addInMap(state.appliedCommands, lastTimestamp, ...lastCommands),
    // apply from left to right
    data: lastCommands.reduce((paths, command) => command.apply(paths), state.data),
    hasChanges: true,
    revertedChanges,
  };
};

export const undoAll = <T>(state: EditorState<T>): EditorState<T> => {
  if (!state.appliedCommands.size) {
    return state;
  }

  const newData = Array.from(state.appliedCommands.keys())
    .sort((a, b) => b - a) // sort from last commands to first
    .reduce((paths, timestamp) => {
      return state.appliedCommands
        .get(timestamp)!
        .reduceRight((paths, command) => command.revert(paths), paths);
    }, state.data);

  return getInitialState(newData);
};

export const reset = <T>(data: T): EditorState<T> => getInitialState(data);
